/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import styled from '@emotion/styled'

import React from 'react'
import { View } from '@components/atoms'
import { grey } from '@mui/material/colors'

import { useAppSelector } from '@redux/index'
import { I18nType } from '@schemas'

export type CategoryType =
	| 'h1'
	| 'h2'
	| 'h3'
	| 'h4'
	| 'h5'
	| 'h6'
	| 's1'
	| 's2'
	| 'p1'
	| 'p2'
	| 'c1'
	| 'c2'
	| 'label'

type HintType =
	| '50'
	| '100'
	| '200'
	| '300'
	| '400'
	| '500'
	| '600'
	| '700'
	| '800'
	| '900'
	| 'A100'
	| 'A200'
	| 'A400'
	| 'A700'

interface PropsType {
	category?: CategoryType
	className?: string
	style?: React.CSSProperties
	underline?: boolean
	hint?: HintType
	children?: string | number
	i18n?: I18nType[]
}

const TextComponent = styled.p({}, (props: PropsType) => {
	return { ...props.style }
})

export const Text = (props: PropsType) => {
	// redux state
	const userLanguage: string = useAppSelector(state => state.user.language)

	// variables
	const category: CategoryType = props.category ? props.category : 's1'
	const hintColor: string = props.hint !== undefined ? grey[props.hint] : ''
	const hintStyle = props.hint !== undefined ? { color: hintColor } : {}
	const underLineStyle =
		props.underline !== undefined ? { textDecoration: 'underline' } : null
	const className = `text ${category} ${props.className ?? ''}`
	const i18n: I18nType[] = props.i18n ?? []
	const thisI18n =
		i18n.length > 0
			? i18n.filter(anI18n => anI18n.isoLanguageCode === userLanguage.toLowerCase())
			: []
	const title: string = thisI18n.length > 0 ? thisI18n[0].title : ''

	return (
		<View css={style}>
			<TextComponent
				className={className}
				style={{ ...props.style, ...hintStyle, ...underLineStyle }}
			>
				{title !== '' ? title : props.children}
			</TextComponent>
		</View>
	)
}

const style = css({
	'.text': {
		width: '100%',
		fontFamily:
			"-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
		lineHeight: 1,
		letterSpacing: 0,

		'&.h1': {
			fontSize: '32px',
			fontWeight: 'bold',
		},

		'&.h2': {
			fontSize: '27px',
			fontWeight: 'bold',
		},

		'&.h3': {
			fontSize: '25px',
			fontWeight: 'bold',
		},

		'&.h4': {
			fontSize: '21px',
			fontWeight: 'bold',
		},

		'&.h5': {
			fontSize: '19px',
			fontWeight: 'bold',
		},

		'&.h6': {
			fontSize: '16px',
			fontWeight: 'bold',
		},

		'&.s1': {
			fontSize: '14px',
			fontWeight: '700',
		},

		'&.s2': {
			fontSize: '13px',
			fontWeight: '700',
		},

		'&.p1': {
			fontSize: '14px',
			fontWeight: '500',
		},

		'&.p2': {
			fontSize: '13px',
			fontWeight: '500',
		},

		'&.c1': {
			fontSize: '12px',
			fontWeight: '300',
		},

		'&.c2': {
			fontSize: '11px',
			fontWeight: '300',
		},

		'&.label': {
			fontSize: '11px',
			fontWeight: 'bold',
		},
	},

	'.text-underline': {
		textDecoration: 'underline',
	},
})
